import type {
  SessionTypesResponse,
  SessionsResponse,
  SessionDetailResponse,
  SessionTypesQueryParams,
  SessionsQueryParams,
  SessionAttemptsResponse,
  SessionScreenshotsQueryParams,
  SessionScreenshotsResponse,
} from './types'
import { AGENT_URL } from '../../config/env'

const API_BASE = `${AGENT_URL}/data`

export async function fetchSessionTypes(
  token: string | null,
  params: SessionTypesQueryParams = {}
): Promise<SessionTypesResponse> {
  const searchParams = new URLSearchParams()

  if (params.limit) searchParams.set('limit', params.limit.toString())
  if (params.cursor) searchParams.set('cursor', params.cursor)
  if (params.sort_by) searchParams.set('sort_by', params.sort_by)
  if (params.sort_order) searchParams.set('sort_order', params.sort_order)

  const url = `${API_BASE}/session-types?${searchParams.toString()}`

  const response = await fetch(url, {
    headers: {
      Authorization: token || '',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch session types: ${response.statusText}`)
  }

  return response.json()
}

export async function fetchSessions(
  token: string | null,
  sessionType: string | null,
  params: SessionsQueryParams = {}
): Promise<SessionsResponse> {
  const searchParams = new URLSearchParams()

  if (params.limit) searchParams.set('limit', params.limit.toString())
  if (params.cursor) searchParams.set('cursor', params.cursor)
  if (params.sort_by) searchParams.set('sort_by', params.sort_by)
  if (params.sort_order) searchParams.set('sort_order', params.sort_order)

  // Filter parameters
  if (params.status) searchParams.set('status', params.status)
  if (params.slug) searchParams.set('slug', params.slug)
  if (params.session_id) searchParams.set('session_id', params.session_id)
  if (params.patient_id) searchParams.set('patient_id', params.patient_id)
  if (params.duty_id) searchParams.set('duty_id', params.duty_id)
  if (params.source_id) searchParams.set('source_id', params.source_id)
  if (params.include_internal) searchParams.set('include_internal', 'true')

  // If sessionType is null, fetch all sessions
  const url = sessionType
    ? `${API_BASE}/session-types/${sessionType}/sessions?${searchParams.toString()}`
    : `${API_BASE}/sessions?${searchParams.toString()}`

  const response = await fetch(url, {
    headers: {
      Authorization: token || '',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch sessions: ${response.statusText}`)
  }

  return response.json()
}

export async function fetchSessionAttempts(
  token: string | null,
  sessionId: string
): Promise<SessionAttemptsResponse> {
  const url = `${API_BASE}/sessions/${sessionId}/attempts`

  const response = await fetch(url, {
    headers: {
      Authorization: token || '',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch session attempts: ${response.statusText}`)
  }

  return response.json()
}

export async function fetchSessionScreenshots(
  token: string | null,
  sessionId: string,
  attemptId: string,
  params: SessionScreenshotsQueryParams = {}
): Promise<SessionScreenshotsResponse> {
  const searchParams = new URLSearchParams()

  if (params.page_size)
    searchParams.set('page_size', params.page_size.toString())
  if (params.offset) searchParams.set('offset', params.offset.toString())

  const queryString = searchParams.toString()
  const url = `${API_BASE}/sessions/${sessionId}/attempts/${attemptId}/screenshots${queryString ? `?${queryString}` : ''}`

  const response = await fetch(url, {
    headers: {
      Authorization: token || '',
    },
  })

  if (!response.ok) {
    throw new Error(
      `Failed to fetch session screenshots: ${response.statusText}`
    )
  }

  return response.json()
}

export async function fetchSessionDetail(
  token: string | null,
  sessionId: string
): Promise<SessionDetailResponse> {
  const url = `${API_BASE}/sessions/${sessionId}`

  const response = await fetch(url, {
    headers: {
      Authorization: token || '',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch session detail: ${response.statusText}`)
  }

  return response.json()
}

export async function retrySession(
  token: string | null,
  sessionId: string
): Promise<{ status: string }> {
  const url = `${API_BASE}/sessions/${sessionId}/retry`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: token || '',
    },
  })

  if (!response.ok) {
    const body = await response.json().catch(() => null)
    const message =
      body && typeof body.error === 'string' ? body.error : response.statusText
    throw new Error(`Failed to retry session: ${message}`)
  }

  return response.json()
}

export async function updateSessionStatus(
  token: string | null,
  sessionId: string,
  status: string
): Promise<{ status: string }> {
  const url = `${API_BASE}/sessions/${sessionId}/status`

  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      Authorization: token || '',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status }),
  })

  if (!response.ok) {
    throw new Error(`Failed to update session status: ${response.statusText}`)
  }

  return response.json()
}

export async function fetchSessionTypeSlugs(
  token: string | null
): Promise<string[]> {
  const url = `${API_BASE}/session-types/list`

  const response = await fetch(url, {
    headers: {
      Authorization: token || '',
    },
  })

  if (!response.ok) {
    throw new Error(
      `Failed to fetch session type slugs: ${response.statusText}`
    )
  }

  const data = await response.json()
  return data.workflow_types || [] // Backend still returns workflow_types field for now
}
